import React from 'react';

import {ELN_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {ChemistryReactionRole} from 'notebook/integrations/chemistry/ChemistryStructuredTableConstants';
import {
  getColumnIndices,
  getMoleculeIdFromEvaluatedCell,
  getRoleFromEvaluatedCell,
} from 'notebook/integrations/chemistry/reaction-scheme/StoichiometryTableHelpers';
import {
  ReactionComponentCountQuery,
  ReactionComponentCountQueryVariables,
} from 'notebook/integrations/chemistry/reactions/__generated/ReactionComponentCountQuery';
import {
  clearCachedReactionComponentCount,
  REACTION_COMPONENT_COUNT_QUERY,
  setCachedReactionComponentCount,
} from 'notebook/integrations/chemistry/reactions/ReactionComponentCountQuery';
import {
  ReactionGroupResponse,
  submitReactionGroup,
} from 'notebook/integrations/chemistry/reactions/ReactionGroupDAO';
import SubmitReactionModal from 'notebook/integrations/chemistry/reactions/submit-modal/SubmitReactionModal';
import type {
  StoichiometryCreateGroupExtras,
  StoichiometryGroupMetadata,
} from 'notebook/integrations/chemistry/StoichiometryTypes';
import {
  GroupActionFunctionParameters,
  NoteLinkedGroup,
  NoteLinkedGroupAdapter,
  ToolbarButtonGroupActionFunction,
} from 'notebook/notes/groups/NoteLinkedGroupConstants';
import {
  getGroupActionButtonDisabledReason,
  getGroupActionButtonDisabledTooltip,
  getGroupActionButtonTableStatus,
  GroupActionDisabledReason,
} from 'notebook/notes/groups/util/GroupActionButtonUtil';
import {ACTION_SUBMIT_REACTION} from 'notebook/store/VersioningConstants';
import {ActionFunctionContext} from 'notebook/structured-tables/action-functions/ActionFunctionContext';
import {
  getSubmitButtonHidden,
  getSubmitButtonLabel,
  getSubmitButtonStyle,
  getSubmitIconForTableStatus,
} from 'notebook/structured-tables/action-functions/makeSimpleActionFunction';
import {
  ActionResultType,
  performServerSideTableWriteActionWithAudits,
} from 'notebook/structured-tables/action-functions/PerformActionFunctions';
import {getStructuredTableAdapters} from 'notebook/structured-tables/StructuredTableAdapters';
import {OutputTableMap} from 'notebook/tables/excess/Evaluator';
import {TableIndicatorStatus} from 'notebook/tables/TableConstants';
import Tooltip from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {FolderItemIcon} from 'ui-library/icons/IconConstants';
import runModal from 'ui-library/runModal';

export async function onInvokeSubmitReactionGroupActionFunction({
  entryOrTemplateId,
  noteLinkedGroup,
  evaluatedTableMap,
  actionFunctionContext,
  noteLinkedGroupAdapter,
}: {
  entryOrTemplateId: string;
  noteLinkedGroup: NoteLinkedGroup<StoichiometryGroupMetadata>;
  evaluatedTableMap: OutputTableMap;
  actionFunctionContext: ActionFunctionContext;
  noteLinkedGroupAdapter: NoteLinkedGroupAdapter<StoichiometryGroupMetadata, StoichiometryCreateGroupExtras>;
}): Promise<void> {
  const existingReactionId = noteLinkedGroup.groupMetadata?.reactionId;

  // For new reactions, prompt the user for a name and folder.
  // For updates (reactionId already exists), skip the modal.
  let extraParams: {folderId?: string; reactionName?: string} | undefined;
  if (!existingReactionId) {
    const modalResult = await runModal(SubmitReactionModal, {}, {title: 'Submit reaction', size: 'md'});
    if (modalResult == null) {
      return;
    }
    extraParams = {
      folderId: modalResult.folderId,
      reactionName: modalResult.reactionName,
    };
  }

  const structuredTables = noteLinkedGroupAdapter.getContainedStructuredTables(noteLinkedGroup);
  const computedTables = structuredTables.map((structuredTable) => evaluatedTableMap[structuredTable.name]);
  const structuredTableAdapters = structuredTables.map(
    (structuredTable) => getStructuredTableAdapters()[structuredTable.kind]
  );

  await performServerSideTableWriteActionWithAudits<ReactionGroupResponse>({
    entryId: entryOrTemplateId,
    loadingScreenMessage: 'Submitting...',
    autoSaverAction: ACTION_SUBMIT_REACTION,
    doPerformAction: async ({dataModsHeader}) => {
      const responseData = await submitReactionGroup(
        entryOrTemplateId,
        noteLinkedGroup,
        evaluatedTableMap,
        actionFunctionContext,
        extraParams,
        dataModsHeader
      );
      return {resultType: ActionResultType.SUCCESS, responseData};
    },
    doPostRefresh: (actionResult) => {
      // Keep count cache in sync after submit so row-deletion detection is immediate.
      const latestReactionId = actionResult.responseData.reactionId;
      // `structuredTables` is intentionally captured from invoke-time: it reflects
      // the rows that were actually submitted, not the post-refresh document state.
      const submittedComponentCount = structuredTables[0]?.rows.length;
      if (submittedComponentCount === undefined) {
        clearCachedReactionComponentCount(latestReactionId);
      } else {
        setCachedReactionComponentCount(latestReactionId, submittedComponentCount);
      }
      return Promise.resolve();
    },
    analyticsActionForPerfTracking: ELN_ANALYTICS_ACTIONS.SUBMITTED_REACTION_GROUP.action,
    structuredTables,
    structuredTableAdapters,
    computedTables,
    tables: [],
  });
}

function hasUnrecognizedMolecules(
  params: GroupActionFunctionParameters<StoichiometryGroupMetadata, StoichiometryCreateGroupExtras>
): boolean {
  const structuredTables = params.noteLinkedGroupAdapter.getContainedStructuredTables(params.noteLinkedGroup);
  for (const table of structuredTables) {
    const evaluatedTable = params.evaluatedTableMap[table.name];
    if (!evaluatedTable) {
      continue;
    }

    const {compoundColumnIndex, roleColumnIndex} = getColumnIndices(table.columns);

    for (const evaluatedRow of evaluatedTable) {
      const role = getRoleFromEvaluatedCell(evaluatedRow[roleColumnIndex]);
      if (role === ChemistryReactionRole.REACTANT || role === ChemistryReactionRole.PRODUCT) {
        const moleculeId = getMoleculeIdFromEvaluatedCell(evaluatedRow[compoundColumnIndex]);
        if (moleculeId == null) {
          return true;
        }
      }
    }
  }
  return false;
}

function hasStoichiometryRowCountMismatch(
  params: GroupActionFunctionParameters<StoichiometryGroupMetadata, StoichiometryCreateGroupExtras>,
  submittedComponentCount: number | null
): boolean {
  if (submittedComponentCount == null) {
    return false;
  }
  const containedStructuredTables = params.noteLinkedGroupAdapter.getContainedStructuredTables(
    params.noteLinkedGroup
  );
  return containedStructuredTables[0]?.rows.length !== submittedComponentCount;
}

function getSubmitReactionTableStatus(
  params: GroupActionFunctionParameters<StoichiometryGroupMetadata, StoichiometryCreateGroupExtras>,
  submittedComponentCount: number | null
): TableIndicatorStatus {
  const baseStatus = getGroupActionButtonTableStatus(params);
  if (baseStatus === TableIndicatorStatus.NO_STATUS) {
    return hasStoichiometryRowCountMismatch(params, submittedComponentCount)
      ? TableIndicatorStatus.UNSUBMITTED_CHANGES
      : TableIndicatorStatus.NO_STATUS;
  }
  return baseStatus;
}

function isSubmitReactionDisabled(
  params: GroupActionFunctionParameters<StoichiometryGroupMetadata, StoichiometryCreateGroupExtras>,
  tableStatus: TableIndicatorStatus
): boolean {
  const disabledReason = getGroupActionButtonDisabledReason(params);
  if (disabledReason != null) {
    // The generic check considers the table "submitted" when row-level flags show
    // no changes.  However, our stoichiometry-specific row-count mismatch may have
    // promoted the status to UNSUBMITTED_CHANGES — in that case, override the
    // generic disable so the user can re-submit.
    if (
      disabledReason === GroupActionDisabledReason.NO_UNSUBMITTED_OR_EDITED_ROWS &&
      tableStatus === TableIndicatorStatus.UNSUBMITTED_CHANGES
    ) {
      return hasUnrecognizedMolecules(params);
    }
    return true;
  }
  return hasUnrecognizedMolecules(params);
}

function getSubmitReactionDisabledTooltip(
  params: GroupActionFunctionParameters<StoichiometryGroupMetadata, StoichiometryCreateGroupExtras>,
  tableStatus: TableIndicatorStatus
): string | null {
  const baseTooltip = getGroupActionButtonDisabledTooltip(params);
  if (baseTooltip != null) {
    // When the generic helpers report "all submitted" but we detected a row-count
    // mismatch (tableStatus promoted to UNSUBMITTED_CHANGES), suppress that tooltip
    // so the user can re-submit.
    const disabledReason = getGroupActionButtonDisabledReason(params);
    if (
      disabledReason === GroupActionDisabledReason.NO_UNSUBMITTED_OR_EDITED_ROWS &&
      tableStatus === TableIndicatorStatus.UNSUBMITTED_CHANGES
    ) {
      return null;
    }
    return baseTooltip;
  }
  if (hasUnrecognizedMolecules(params)) {
    return 'All molecules must be linked to registered entities before submitting';
  }
  return null;
}

function SubmitReactionToolbarButton({
  params,
}: {
  params: GroupActionFunctionParameters<StoichiometryGroupMetadata, StoichiometryCreateGroupExtras>;
}): JSX.Element {
  const reactionId = params.noteLinkedGroup.groupMetadata.reactionId;
  const reactionCountResult = useSkippableBenchlingQuery<
    ReactionComponentCountQuery,
    ReactionComponentCountQueryVariables
  >(REACTION_COMPONENT_COUNT_QUERY, {
    variables: {reactionId: reactionId ?? ''},
    skip: reactionId == null,
  });
  const submittedComponentCount =
    reactionCountResult.type === QueryResultType.SUCCESS &&
    reactionCountResult.data.reaction.__typename === 'ReactionNode'
      ? reactionCountResult.data.reaction.numberOfComponents
      : null;
  const tableStatus = getSubmitReactionTableStatus(params, submittedComponentCount);
  const disabled = isSubmitReactionDisabled(params, tableStatus);
  const tooltipText = getSubmitReactionDisabledTooltip(params, tableStatus);
  const label = getSubmitButtonLabel(tableStatus);

  return (
    // eslint-disable-next-line benchlint/no-eqeqeq-null
    <Tooltip title={tooltipText ?? label} disabled={tooltipText === null}>
      <span>
        <Button
          buttonStyle={getSubmitButtonStyle(tableStatus, ButtonStyle.SECONDARY)}
          className="u-mini-spacing-left"
          disabled={disabled}
          icon={getSubmitIconForTableStatus(tableStatus, FolderItemIcon.Reaction)}
          iconAltText={tooltipText ?? label}
          text={label}
          onClick={() => {
            onInvokeSubmitReactionGroupActionFunction(params).catch(() => undefined);
          }}
          size={ButtonSize.MINI}
        />
      </span>
    </Tooltip>
  );
}

const submitReactionGroupActionFunction: ToolbarButtonGroupActionFunction<
  StoichiometryGroupMetadata,
  StoichiometryCreateGroupExtras
> = {
  renderToolbarButton: (
    params: GroupActionFunctionParameters<StoichiometryGroupMetadata, StoichiometryCreateGroupExtras>
  ): JSX.Element | null => {
    const tableStatus = getGroupActionButtonTableStatus(params);
    if (getSubmitButtonHidden(tableStatus, params.isCollapsed)) {
      return null;
    }
    return <SubmitReactionToolbarButton params={params} />;
  },
};

export default submitReactionGroupActionFunction;
